import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { LowerCasePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';
import { Md5 } from 'ts-md5';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { FormComponent } from '@atoms/form/form.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { OtpInputComponent } from '@atoms/otp-input/otp-input.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { StrongComponent } from '@atoms/strong/strong.component';
import { UnderlineLinkComponent } from '@atoms/underline-link/underline-link.component';
import { ModalMainContentComponent } from '@molecules/modal/modal-main-content/modal-main-content.component';
import { ModalHeaderComponent } from '@molecules/modal/modal-header/modal-header.component';
import { ModalMainComponent } from '@molecules/modal/modal-main/modal-main.component';
import { ModalTitleComponent } from '@molecules/modal/modal-title/modal-title.component';
import { HidePhonePipe } from '@pipes/hide-phone.pipe';
import { GlobalsService } from '@services/globals.service';
import { SaveFormService } from '@services/save-form.service';
import { FinancingFormService } from '@services/financing-form.service';
import { GtmService } from '@services/gtm.service';
import { CarValuationService } from '@services/car-valuation.service';
import { OnlineFormService } from '@services/online-form.service';
import { CarFormData } from '@models/car-valuation.model';
import { SaveFormRegistrationRequest, SaveFormSaveFinancingForm } from '@models/save-form.model';
import { OnlineFormData } from '@models/online-form.model';

@Component({
  selector: 'app-confirm-modal',
  imports: [LowerCasePipe, ReactiveFormsModule, TranslateModule, ButtonComponent, FormComponent, GridComponent, OtpInputComponent, ParagraphComponent, StrongComponent, UnderlineLinkComponent, ModalMainContentComponent, ModalHeaderComponent, ModalMainComponent, ModalTitleComponent, HidePhonePipe],
  templateUrl: './confirm-modal.component.html',
  styleUrl: './confirm-modal.component.scss',
})
export class ConfirmModalComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private globalsService = inject(GlobalsService);
  private saveFormService = inject(SaveFormService);
  private financingFormService = inject(FinancingFormService);
  private carValuationService = inject(CarValuationService);
  private onlineFormService = inject(OnlineFormService);
  private gtmService = inject(GtmService);
  private globals = inject(Globals);

  @Input() data?: { email?: string; phone?: string; phoneCode?: string; type: boolean; step: number; verifyAgain: false };
  public form = new FormGroup({
    otp: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern('^.{4}$')] }),
  });
  public email!: string;
  private id = this.activatedRoute.snapshot.queryParamMap.get('id') || '';

  send(): void {
    if (this.form.invalid || !this.email) return;
    const email = this.email;
    const phone = this.data?.phone;
    const hashedEmail = email ? Md5.hashStr(email) : undefined;
    const hashedPhone = phone ? Md5.hashStr(phone) : undefined;
    const request = {
      email: this.email,
      token: this.form.controls['otp'].value,
    };
    this.saveFormService
      .validateUser(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response && response.code === 201) {
            if (!this.data?.verifyAgain) {
              this.globalsService.setHeaderHashLogged(response.message.token);
              this.globalsService.setHeaderWithMD5Hash(this.email);
              const modalData = {
                email: this.email,
                phone: this.data?.phone,
                phoneCode: this.data?.phoneCode,
                type: this.data?.type,
                name: '',
              };
              if (response.message.token)
                this.saveFormService
                  .generalInfo(true, false, modalData)
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: (response) => {
                      const dataLayer = {
                        event: 'page_view',
                        pageURL: '/cz/dekovacka-ukladani-formu-' + (this.data?.step ?? 1),
                        pageType: 'Form-1',
                        pageTitle: 'Poděkování formulář uložen ' + (this.data?.step ?? 1),
                        purchase_type: this.data?.type ? 'phone' : 'email',
                        data: {
                          splatka: this.financingFormService.product()?.monthlyPayment,
                          akontace: this.financingFormService.carContractData()?.advance,
                          kategorie_zakazniku: this.financingFormService.carContractData()?.loanType,
                          doba_splaceni: this.financingFormService.carContractData()?.numberOfMonths,
                          orderId: response?.message.formId,
                          email: this.email ? hashedEmail : undefined,
                          phone: this.data?.phone ? hashedPhone : undefined,
                        },
                      };
                      this.gtmService.pushGtm(dataLayer);
                    },
                  });
            } else {
              this.globalsService.setHeaderHashLogged(response.message.token);
              this.saveFormService
                .fetchForm(this.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response) => {
                  const data = response.message;
                  if (data) {
                    this.saveFormService.editCustomerData.set(data[0].customer);
                    if (Number(data[0].formType) === 16) {
                      this.saveFormService.formType.set('financingForm');
                      let formData = data[0].formData;
                      let formDataParsed: SaveFormSaveFinancingForm | undefined;
                      if (formData && typeof formData === 'string') formDataParsed = JSON.parse(formData.replace(/'/g, '"'));
                      if (formDataParsed) {
                        response.message[0].formData = formDataParsed;
                        this.globalsService.setHeaderWithMD5Hash(data[0].customer.email!);
                        this.financingFormService.product.set(formDataParsed.product);
                        this.financingFormService.formId.set(this.id);
                        this.financingFormService.contactInfoData.set(formDataParsed.contactInformation);
                        this.financingFormService.carContractData.set({ carId: formDataParsed.carId, numberOfMonths: formDataParsed.numberOfMonths, advance: formDataParsed.advance });
                        let activeRoute = 'financingFormProducts';
                        if (this.financingFormService.product()) {
                          if (this.financingFormService.contactInfoData()?.name) {
                            activeRoute = 'financingFormContactInformations';
                          }
                        }
                        this.router.navigate([this.globals.routes()[activeRoute].url], {
                          queryParams: { car: formDataParsed.carId },
                          fragment: 'months=' + formDataParsed.numberOfMonths + '&advance=' + formDataParsed.advance + '&loan-type=' + (formDataParsed.contactInformation?.loanType ?? formDataParsed.loanType) + '&saved-form=true',
                        });
                      }
                    } else if (Number(data[0].formType) === 4) {
                      let formData = data[0].formData;
                      let formDataParsed: CarFormData | undefined;
                      if (formData && typeof formData === 'string') formDataParsed = JSON.parse(formData.replace(/'/g, '"'));
                      this.saveFormService.formType.set('carValuationForm');
                      this.carValuationService.formId.set(this.id);
                      if (formDataParsed) {
                        response.message[0].formData = formDataParsed;
                        this.globalsService.setHeaderWithMD5Hash(data[0].customer.email!);
                        this.carValuationService.carValuationData.set(formDataParsed);
                        let activeRoute = 'carValuation';
                        if (this.carValuationService.carValuationData().specificationCarValid) {
                          activeRoute = 'carValuationSpecification';
                          if (this.carValuationService.carValuationData().equipmentValid) {
                            activeRoute = 'carValuationEquipment';
                            if (this.carValuationService.carValuationData().carConditionValid) {
                              activeRoute = 'carValuationCondition';
                              if (this.carValuationService.carValuationData().carPhotosValid) {
                                activeRoute = 'carValuationPhotos';
                                if (this.carValuationService.carValuationData().contactInformationValid) {
                                  activeRoute = 'carValuationContactInformations';
                                }
                              }
                            }
                          }
                        }
                        this.router.navigate([this.globals.routes()[activeRoute].url]);
                      }
                    } else if (Number(data[0].formType) === 1 || Number(data[0].formType) === 2) {
                      this.saveFormService.formType.set(Number(data[0].formType) === 2 ? 'cash' : 'installment');
                      this.onlineFormService.formId.set(this.id);
                      let formData = data[0].formData;
                      let formDataParsed: any | undefined;
                      if (formData && typeof formData === 'string') formDataParsed = JSON.parse(formData.replace(/'/g, '"'));
                      if (formDataParsed) {
                        response.message[0].formData = formDataParsed;
                        this.globalsService.setHeaderWithMD5Hash(data[0].customer.email!);
                        this.onlineFormService.formData.set(formDataParsed as OnlineFormData);
                        let activeRoute = 'onlineForm';
                        if (this.onlineFormService.formData().paymentType) {
                          activeRoute = 'onlineFormPaymentOption';
                          if (this.onlineFormService.formData().deliveryType) {
                            activeRoute = 'onlineFormDeliveryType';
                            if (this.onlineFormService.formData().accessories) {
                              activeRoute = 'onlineFormAccessories';
                              if (this.onlineFormService.formData().contactInfo) {
                                activeRoute = 'onlineFormContactInformations';
                              }
                            }
                          }
                        }
                        this.router.navigate([this.globals.routes()[activeRoute].url], { queryParams: { car: formDataParsed.carId } });
                      }
                    }
                  }
                });
              //this.router.navigate([this.globals.routes()['financingFormSavedForm'].url], { queryParams: { id: this.id, loaded: true } });
            }
          }
        },
      });
  }

  registerUser(): void {
    if (!this.data) return;
    const phoneCode: number | undefined = this.data.phoneCode ? Number(this.data.phoneCode.replace('+', '')) : undefined;
    const phoneCountry: number | undefined = this.globalsService.searchPhoneCountryCode(phoneCode);
    if (this.data.type && !phoneCountry) return;
    const registerUser: SaveFormRegistrationRequest = {
      email: this.email,
      firstName: 'Moto',
      lastName: 'Techna',
      sendSMS: this.data.type,
      phone: this.data.phone ? Number(this.data.phone) : undefined,
      phoneCountryId: phoneCountry,
      sendCode: true,
    };
    this.saveFormService.registerUser(registerUser).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngOnInit(): void {
    if (!this.data) return;
    this.email = this.data.email ?? this.data.phoneCode?.replace('+', '') + '' + this.data.phone + '@mototechna.cz';
    this.registerUser();
  }
}
